import React, { useState, useRef, useEffect } from 'react';
import { Plus, FolderGit2, ChevronDown, Info } from 'lucide-react';
import { CORE_SERVICE_TYPES } from '../../config/serviceTypes';

const NewServiceDropdown = ({ onSelectType, onCreateProject, onShowInfo }) => {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setOpen(!open)}
                aria-expanded={open}
                aria-controls="new-service-menu"
                className="flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-indigo-700 transition-all shadow-sm hover:shadow-md"
            >
                <Plus className="w-4 h-4" />
                New
                <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div id="new-service-menu" className="absolute right-0 top-12 w-72 bg-card border border-inner rounded-2xl shadow-2xl py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                    {/* Service Types */}
                    <div className="px-3 py-2 text-[11px] font-bold text-muted uppercase tracking-widest">
                        Create a Service
                    </div>
                    {CORE_SERVICE_TYPES.map((type) => {
                        const Icon = type.icon;
                        return (
                            <div key={type.id} className="group flex items-stretch pr-2 hover:bg-hover transition">
                                <button
                                    onClick={() => {
                                        onSelectType(type.id);
                                        setOpen(false);
                                    }}
                                    className="flex flex-1 items-start gap-3 px-4 py-3 text-left"
                                >
                                    <div className={`p-1.5 rounded-lg ${type.bg} mt-0.5`}>
                                        <Icon className={`w-4 h-4 ${type.color}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-bold text-primary">{type.label}</span>
                                            {type.includedWithPlan && (
                                                <span className="text-[10px] font-bold text-green bg-green/10 border border-green/20 px-1.5 py-0.5 rounded-full">INCLUDED</span>
                                            )}
                                        </div>
                                        <p className="text-xs text-muted mt-0.5 leading-relaxed">{type.menuDescription}</p>
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onShowInfo?.(type.id)}
                                    className="self-center rounded-lg p-2 text-subtle opacity-70 transition hover:bg-input hover:text-indigo focus-visible:opacity-100 group-hover:opacity-100"
                                    aria-label={`Learn more about ${type.label}`}
                                >
                                    <Info className="w-4 h-4" />
                                </button>
                            </div>
                        );
                    })}

                    {/* Divider */}
                    <div className="border-t border-inner my-1" />

                    {/* Project */}
                    <div className="px-3 py-2 text-[11px] font-bold text-muted uppercase tracking-widest">
                        Other
                    </div>
                    <button
                        onClick={() => {
                            onCreateProject();
                            setOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-hover transition text-left"
                    >
                        <div className="p-1.5 rounded-lg bg-root border border-inner">
                            <FolderGit2 className="w-4 h-4 text-muted" />
                        </div>
                        <div>
                            <span className="text-sm font-bold text-primary">Project</span>
                            <p className="text-xs text-muted mt-0.5">Connect a GitHub repository</p>
                        </div>
                    </button>
                </div>
            )}
        </div>
    );
};

export default NewServiceDropdown;
